const MAX_TIMEOUT_DELAY = 2147483647;

class AnnouncementBarSchedule extends HTMLElement {
  connectedCallback() {
    this.startDate = this.dataset.start ? new Date(this.dataset.start) : null;
    this.endDate = this.dataset.end ? new Date(this.dataset.end) : null;
    this.isSticky = this.dataset.sticky === 'true';
    this.marqueeEnabled = this.dataset.marquee === 'true';
    this.marqueeSpeed = Number(this.dataset.marqueeSpeed) || 60;

    this.update();

    if (this.isSticky && typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(this.#updateStickyOffset);
      this.resizeObserver.observe(this);
    }

    if (this.marqueeEnabled) {
      this.#updateMarquee();

      this.onWindowResize = () => {
        clearTimeout(this.marqueeResizeTimeout);
        this.marqueeResizeTimeout = setTimeout(this.#updateMarquee, 150);
      };
      window.addEventListener('resize', this.onWindowResize);
    }

    this.#reconcileActiveSlide();

    this.countdownEls = this.querySelectorAll('[data-role="countdown"]');
    if (this.countdownEls.length > 0) {
      this.#updateCountdown();
      this.countdownInterval = setInterval(this.#updateCountdown, 1000);
    }
  }

  disconnectedCallback() {
    clearTimeout(this.timeoutId);
    clearTimeout(this.marqueeResizeTimeout);
    clearInterval(this.countdownInterval);
    this.resizeObserver?.disconnect();
    if (this.onWindowResize) window.removeEventListener('resize', this.onWindowResize);
    document.body.style.removeProperty('--announcement-bar-sticky-height');
  }

  update = () => {
    if (window.Shopify?.designMode) {
      this.hidden = false;
      this.#updateStickyOffset();
      return;
    }

    const now = new Date();
    const isActive = (!this.startDate || now >= this.startDate) && (!this.endDate || now <= this.endDate);

    this.hidden = !isActive;
    this.#updateStickyOffset();
    this.#scheduleNextUpdate(now);
  };

  #scheduleNextUpdate(now) {
    clearTimeout(this.timeoutId);

    let nextChange = null;
    if (this.startDate && now < this.startDate) {
      nextChange = this.startDate;
    } else if (this.endDate && now <= this.endDate) {
      nextChange = new Date(this.endDate.getTime() + 1000);
    }

    if (!nextChange) return;

    const delay = Math.min(nextChange.getTime() - now.getTime(), MAX_TIMEOUT_DELAY);
    this.timeoutId = setTimeout(this.update, Math.max(delay, 0));
  }

  #updateStickyOffset = () => {
    if (!this.isSticky || this.hidden) {
      document.body.style.removeProperty('--announcement-bar-sticky-height');
    } else {
      document.body.style.setProperty('--announcement-bar-sticky-height', `${this.offsetHeight}px`);
    }
  };

  #updateArrowsVisibility = () => {
    const arrows = this.querySelector('slideshow-arrows');
    if (!arrows) return;

    const visibleCount = Array.from(this.querySelectorAll('.announcement-bar__slide')).filter(
      (slide) => slide.style.display !== 'none'
    ).length;

    arrows.style.display = visibleCount > 1 ? '' : 'none';
  };

  #reconcileActiveSlide = () => {
    const slides = Array.from(this.querySelectorAll('.announcement-bar__slide'));
    if (slides.length === 0) return;

    const visibleSlides = slides.filter((slide) => slide.style.display !== 'none');
    const hasVisibleActive = visibleSlides.some((slide) => slide.getAttribute('aria-hidden') === 'false');
    if (!hasVisibleActive) {
      slides.forEach((slide) => slide.setAttribute('aria-hidden', 'true'));

      if (visibleSlides.length > 0) {
        visibleSlides[0].setAttribute('aria-hidden', 'false');

        const component = this.querySelector('announcement-bar-component');
        if (component) component.current = slides.indexOf(visibleSlides[0]);
      }
    }

    this.#updateArrowsVisibility();
  };

  #hideSlide = (slide) => {
    if (!slide) return;

    slide.style.display = 'none';

    if (slide.getAttribute('aria-hidden') !== 'false') return;

    slide.setAttribute('aria-hidden', 'true');

    const slides = Array.from(this.querySelectorAll('.announcement-bar__slide'));
    const nextVisible = slides.find((candidate) => candidate !== slide && candidate.style.display !== 'none');
    if (!nextVisible) return;

    nextVisible.setAttribute('aria-hidden', 'false');

    const component = this.querySelector('announcement-bar-component');
    if (component) component.current = slides.indexOf(nextVisible);
  };

  #getCETMidnight = () => {
    const now = new Date();
    const utc = now.getTime() + now.getTimezoneOffset() * 60000;
    const cetNow = new Date(utc + 60 * 60000);
    const midnight = new Date(cetNow);
    midnight.setHours(24, 0, 0, 0);
    return new Date(now.getTime() + (midnight.getTime() - cetNow.getTime()));
  };

  #updateCountdown = () => {
    const pad = (n) => String(n).padStart(2, '0');

    this.countdownEls.forEach((el) => {
      const repeat24h = el.dataset.repeat24h === 'true';
      const end = repeat24h ? this.#getCETMidnight() : el.dataset.end ? new Date(el.dataset.end) : this.endDate;
      if (!end) return;

      const start = el.dataset.start ? new Date(el.dataset.start) : null;
      const slide = el.closest('.announcement-bar__slide');
      const now = Date.now();

      if (start && now < start.getTime() && !window.Shopify?.designMode) {
        this.#hideSlide(slide);
        return;
      }

      const remaining = Math.max(end.getTime() - now, 0);

      if (!repeat24h && remaining <= 0 && el.dataset.hideAtZero === 'true' && !window.Shopify?.designMode) {
        this.#hideSlide(slide);
        return;
      }

      if (slide) slide.style.display = '';

      const days = Math.floor(remaining / 86400000);
      const hours = Math.floor((remaining % 86400000) / 3600000);
      const minutes = Math.floor((remaining % 3600000) / 60000);
      const seconds = Math.floor((remaining % 60000) / 1000);

      el.textContent =
        days > 0 ? `${days}:${pad(hours)}:${pad(minutes)}:${pad(seconds)}` : `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
    });

    this.#updateArrowsVisibility();
  };

  #updateMarquee = () => {
    const isMobile = window.innerWidth <= 749;

    this.querySelectorAll('.announcement-bar__text').forEach((text) => {
      const slide = text.closest('.announcement-bar__slide');
      if (!slide) return;

      if (!isMobile) {
        text.classList.remove('is-marquee');
        text.style.whiteSpace = '';
        text.style.removeProperty('--marquee-duration');
        return;
      }

      text.style.whiteSpace = 'nowrap';
      const overflowing = text.scrollWidth > slide.clientWidth;
      text.classList.toggle('is-marquee', overflowing);

      if (overflowing) {
        const duration = Math.max(text.scrollWidth / this.marqueeSpeed, 1);
        text.style.setProperty('--marquee-duration', `${duration}s`);
      } else {
        text.style.whiteSpace = '';
      }
    });
  };
}

if (!customElements.get('announcement-bar-schedule')) {
  customElements.define('announcement-bar-schedule', AnnouncementBarSchedule);
}
